/**
 * LEADS API - The ONLY place where backend calls happen
 *
 * There are exactly 2 functions:
 * 1. searchCompanies() - All company searches go through here
 * 2. searchPeople() - All people searches go through here
 *
 * Everything else (imports, saves, etc.) uses these same functions
 * or are simple CRUD operations.
 */

import type { CompanyData, CompanyFilters, PersonData, PeopleFilters, AIFilter, SearchState, CountryFilter } from "./types"
import { buildAiFilterProgressUrl } from "@/lib/ai-filter-progress-url"
import { getFreshAccessToken } from "@/lib/supabase"

/**
 * Build headers with JWT auth token for leads-server-2 requests.
 */
async function authHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = { "Content-Type": "application/json" }
    const token = await getFreshAccessToken()
    if (token) {
        headers["Authorization"] = `Bearer ${token}`
    }
    return headers
}

// Cache for countries/regions data
let countriesData: { countries: CountryFilter[]; regions: { name: string; apiCode: string; countries: CountryFilter[] }[] } | null = null

async function loadCountriesData() {
    if (countriesData) return countriesData
    try {
        const res = await fetch("/countries.json")
        countriesData = await res.json()
        return countriesData
    } catch (err) {
        console.error("Failed to load countries.json:", err)
        return { countries: [], regions: [] }
    }
}

/**
 * Expand regions to their constituent countries
 * If a CountryFilter has apiCode starting with "X-", it's a region and needs expansion
 */
async function expandRegionsToCountries(filters: CountryFilter[]): Promise<CountryFilter[]> {
    if (!filters || filters.length === 0) return []

    const data = await loadCountriesData()
    const expanded: CountryFilter[] = []

    for (const filter of filters) {
        if (filter.apiCode.startsWith("X-")) {
            // It's a region - find and expand to its countries
            const region = data?.regions?.find(r => r.apiCode === filter.apiCode)
            if (region) {
                expanded.push(...region.countries)
            }
        } else {
            // It's a regular country - keep as-is
            expanded.push(filter)
        }
    }

    return expanded
}

// Get API URL from environment - defaults to leads-server-2
const API_URL = process.env.NEXT_PUBLIC_ASYNC_LEAD_API_URL_2 || "http://0.0.0.0:8087"

// ============================================
// ACCELERATOR BATCHES
// ============================================

export interface AcceleratorBatch {
    batch: string
    numCompanies: number
}

interface AcceleratorData {
    acceleratorSlug: string
    acceleratorName: string
    batches: AcceleratorBatch[]
}

let _acceleratorBatchCache: AcceleratorBatch[] | null = null

/**
 * Fetch available YC batches from leads-server-2 (with auth).
 * Cached after first successful fetch.
 */
export async function fetchAcceleratorBatches(): Promise<AcceleratorBatch[]> {
    // Only short-circuit on a populated cache — an empty array is truthy in JS,
    // which previously poisoned the cache (if Fiber ever returned empty, all
    // subsequent calls skipped the fetch forever).
    if (_acceleratorBatchCache && _acceleratorBatchCache.length > 0) return _acceleratorBatchCache

    const response = await fetch(`${API_URL}/companies/accelerator-batches`, {
        headers: await authHeaders()
    })
    if (!response.ok) throw new Error("Failed to fetch accelerator batches")

    const data: AcceleratorData[] = await response.json()
    const yc = data.find(a => a.acceleratorSlug === "ycombinator")
    const batches = yc?.batches || []
    if (batches.length > 0) _acceleratorBatchCache = batches
    return batches
}

// ============================================
// MAIN SEARCH FUNCTIONS
// ============================================

export interface SearchCompaniesParams {
    filters: CompanyFilters
    page?: number
    limit?: number
    aiFilters?: AIFilter[]
    aiMatchesOnly?: boolean
    signal?: AbortSignal
    /** Fast-path mirror of `SearchPeopleParams.preview` — see that comment. */
    preview?: boolean
}

export interface SearchCompaniesResult {
    companies: CompanyData[]
    totalResults: number
    totalPages: number
    currentPage: number
    sessionId: string
}

/**
 * Transform raw company data from backend to CompanyData format
 */
function transformCompany(company: any): CompanyData {
    // Build LinkedIn URL from positive linkedin_id (it comes as a string like "-1359736")
    const linkedinIdRaw = company.linkedin_id ? parseInt(String(company.linkedin_id), 10) : null
    const linkedinId = linkedinIdRaw ? Math.abs(linkedinIdRaw) : null
    const linkedinUrl = linkedinId ? `https://www.linkedin.com/company/${linkedinId}` : ""

    // Website/domain is null in the data - would need to be added to OpenSearch index
    const website = company.website || company.domain || company.cb_url || company.homepage_url || ""

    return {
        company_name: company.company_name || company.preferred_name || "Unknown",
        company_description: company.company_description || company.li_description || company.short_description || "",
        website: website,
        linkedin_url: linkedinUrl,
        employee_count: company.employee_count ? String(company.employee_count) : undefined,
        location: company.location?.city ? `${company.location.city}, ${company.location.country_code}` : undefined,
        founded_year: company.founded_on ? new Date(company.founded_on).getFullYear() : undefined,
        funding_round: company.stage || undefined,
        industry: company.fiber_industries?.[0] || undefined,
        crunchbase_industry: company.crunchbase_industry || undefined,
        linkedin_industry: company.linkedin_industry || undefined,
        li_follower_count: company.li_follower_count,
        linkedin_id: linkedinId
    }
}

/**
 * Search for companies using OpenSearch
 * This is the ONLY function that calls the company search backend
 *
 * Fetches a single page of results. Call with different page numbers to paginate.
 */
export async function searchCompanies(params: SearchCompaniesParams): Promise<SearchCompaniesResult> {
    const { filters, page = 1, limit = 25, signal, preview = false } = params

    console.log("[api.ts] searchCompanies called with:", { page, limit, preview })

    // Expand regions (e.g. European Union) to their constituent countries
    const expandedCountries = await expandRegionsToCountries(filters.company_countries || [])
    const expandedExcludeCountries = await expandRegionsToCountries(filters.excludeCompanyCountries || [])

    // Transform frontend filters to backend format
    const backendFilters = {
        fundingRounds: filters.fundingRounds || [],
        investors: filters.investors || [],
        fiberIndustries: [],
        crunchbaseIndustries: filters.crunchbaseIndustries || [],
        linkedinIndustries: filters.linkedinIndustries || [],
        employeeCounts: filters.employeeCounts || [],
        revenueRanges: filters.revenueRanges || [],
        company_countries: expandedCountries,
        company_location_filters: [...(filters.company_locations || []), ...(filters.company_states || [])],
        foundedYears: filters.foundedYears || [],
        filterMode: filters.filterMode || "venture",
        include_companies: filters.include_companies || [],
        exclude_list_ids: filters.excludeListIds || [],
        naics_codes: filters.naicsCodes || [],
        keywords: filters.keywords || [],
        companyKeywordBoost: filters.companyKeywordBoost || [],
        accelerators: filters.accelerators || [],
        exclude_crunchbase_industries: filters.excludeCrunchbaseIndustries || [],
        exclude_linkedin_industries: filters.excludeLinkedinIndustries || [],
        exclude_company_countries: expandedExcludeCountries
    }

    try {
        const headers = await authHeaders()
        let response: Response
        let data: any

        if (preview) {
            const rowsUrl = `${API_URL}/companies/search?page=${page}&page_size=${limit}&preview=true`
            const countUrl = `${API_URL}/companies/count`
            const [rowsRes, countRes] = await Promise.all([
                fetch(rowsUrl, { method: "POST", headers, body: JSON.stringify({ filters: backendFilters }), signal }),
                fetch(countUrl, { method: "POST", headers, body: JSON.stringify({ filters: backendFilters }), signal }),
            ])
            if (!rowsRes.ok) {
                throw new Error(`Search failed: ${rowsRes.status} - ${await rowsRes.text()}`)
            }
            const rowsData = await rowsRes.json()
            const countData = countRes.ok ? await countRes.json() : { count: 0 }
            data = {
                ...rowsData,
                total: typeof countData.count === "number" ? countData.count : 0,
            }
            response = rowsRes
        } else {
            response = await fetch(`${API_URL}/companies/search?page=${page}&page_size=${limit}`, {
                method: "POST",
                headers,
                body: JSON.stringify({ filters: backendFilters }),
                signal
            })

            if (!response.ok) {
                const errorText = await response.text()
                throw new Error(`Search failed: ${response.status} - ${errorText}`)
            }

            data = await response.json()
        }
        console.log("[api.ts] searchCompanies response (preview=" + preview + "):", data)

        // Log first company to see all available fields
        if (data.data?.[0]) {
            console.log("[api.ts] Sample company fields:", Object.keys(data.data[0]))
        }

        const companies: CompanyData[] = data.data.map(transformCompany)
        const sessionId = `session-${Date.now()}`

        return {
            companies,
            totalResults: data.total,
            totalPages: data.total_pages,
            currentPage: data.page,
            sessionId
        }
    } catch (error) {
        // Aborts are expected (caller cancelled — e.g. filter changed mid-fetch).
        // Logging them surfaces a Console AbortError in Next.js dev overlay
        // even though the caller handles it; suppress and re-throw silently.
        if ((error as { name?: string } | null)?.name !== "AbortError") {
            console.error("[api.ts] searchCompanies error:", error)
        }
        throw error
    }
}

export interface SearchPeopleParams {
    filters: PeopleFilters
    page?: number
    limit?: number
    aiFilters?: AIFilter[]
    aiMatchesOnly?: boolean
    perCompanyLimit?: number
    excludeLinkedInUrls?: string[]
    includeLinkedInUrls?: string[]
    signal?: AbortSignal
    /** Fast-path: hit /people/search?preview=true (trimmed _source, no
     *  track_total_hits, no Step 1.5/1.55 boost resolution) and run /people/count
     *  in parallel for the total. Same 25 rows + same count number as the default
     *  path, just typically 5–20× faster on filter shapes with boost phrases.
     *  Use for unsaved/draft preview surfaces; default to false for saved
     *  list pagination where exact behavior parity matters. */
    preview?: boolean
}

export interface SearchPeopleResult {
    people: PersonData[]
    totalResults: number
    totalPages: number
    currentPage: number
    sessionId: string
    companyIdsCount?: number
    totalCompaniesMatched?: number
}

/**
 * Search for people using OpenSearch
 * This is the ONLY function that calls the people search backend
 */
export async function searchPeople(params: SearchPeopleParams): Promise<SearchPeopleResult> {
    const { filters, page = 1, limit = 25, aiFilters, aiMatchesOnly, perCompanyLimit, excludeLinkedInUrls, includeLinkedInUrls, signal, preview = false } = params

    console.log("[api.ts] searchPeople called with:", params)
    console.log("[api.ts] Excluding", excludeLinkedInUrls?.length || 0, "LinkedIn URLs")

    try {
        // Expand regions to their constituent countries
        const expandedCountries = await expandRegionsToCountries(filters.person_countries || [])
        const expandedExcludeCountries = await expandRegionsToCountries(filters.exclude_person_countries || [])
        console.log(
            "[api.ts] Expanded countries:",
            expandedCountries.map(c => c.apiCode)
        )

        // Merge state filters into location arrays (backend handles type differentiation)
        const mergedCompanyLocations = [...(filters.company_locations || []), ...(filters.company_states || [])]
        const mergedPersonLocations = [...(filters.person_locations || []), ...(filters.person_states || [])]

        // Build company filters from people filters
        const companyFilters = {
            fundingRounds: filters.company_stage || [],
            investors: filters.company_investors || [],
            fiberIndustries: [],
            crunchbaseIndustries: filters.company_industries || [],
            linkedinIndustries: filters.company_linkedin_industries || [],
            employeeCounts: filters.company_employee_counts || [],
            revenueRanges: filters.company_revenue_ranges || [],
            company_location_filters: mergedCompanyLocations,
            foundedYears: [],
            filterMode: filters.company_filter_mode || "venture",
            include_companies: filters.current_companies || [],
            exclude_list_ids: filters.excludeCompanyListIds || [],
            naics_codes: filters.company_naics_codes || [],
            accelerators: filters.company_accelerators || [],
            exclude_crunchbase_industries: filters.exclude_company_industries || [],
            exclude_linkedin_industries: filters.exclude_company_linkedin_industries || []
        }

        // Build people filters
        const peopleFilters = {
            person_countries: expandedCountries, // Use expanded countries (regions -> individual countries)
            person_locations: mergedPersonLocations,
            person_titles: filters.person_titles || [],
            person_title_boost: filters.person_title_boost || [],
            person_title_boost_primary: filters.person_title_boost_primary || [],
            person_title_boost_secondary: filters.person_title_boost_secondary || [],
            person_seniorities: filters.person_seniorities || [],
            person_functions: [],
            alumni_schools: filters.alumni_schools || [],
            fields_of_study: filters.fields_of_study || [],
            years_out_of_college: filters.years_out_of_college || [],
            years_at_current_company: filters.years_at_current_company || [],
            linkedin_summary_keywords: filters.linkedin_summary_keywords || [],
            min_linkedin_connections: filters.min_linkedin_connections,
            contact_email_status: [],
            current_companies: filters.current_companies || [],
            previous_companies: filters.previous_companies || [],
            company_location_filters: mergedCompanyLocations,
            company_employee_counts: filters.company_employee_counts || [],
            company_revenue_ranges: filters.company_revenue_ranges || [],
            company_stage: filters.company_stage || [],
            company_investors: filters.company_investors || [],
            company_industries: filters.company_industries || [],
            company_fiber_industries: [],
            company_linkedin_industries: filters.company_linkedin_industries || [],
            company_accelerators: filters.company_accelerators || [],
            company_filter_mode: filters.company_filter_mode || "venture",
            company_keywords: filters.company_keywords || [],
            company_keyword_boost: filters.company_keyword_boost || [],
            per_company_limit: perCompanyLimit || null,
            exclude_company_list_ids: filters.excludeCompanyListIds || [],
            exclude_linkedin_urls: excludeLinkedInUrls || [],
            include_linkedin_urls: includeLinkedInUrls || [],
            exclude_person_titles: filters.exclude_person_titles || [],
            exclude_person_countries: expandedExcludeCountries,
            exclude_company_industries: filters.exclude_company_industries || [],
            exclude_company_linkedin_industries: filters.exclude_company_linkedin_industries || []
        }

        const requestBody = {
            company_filters: companyFilters,
            people_filters: peopleFilters
        }

        // Preview path: fire rows (preview=true, trimmed _source, no boost
        // resolution) AND /people/count in parallel. The rows endpoint returns
        // total=0 in preview mode, so we synthesize the response by stitching
        // the count from /people/count over the rows payload. Net effect: rows
        // arrive in ~500-1000ms instead of 5-30s for boost-heavy filters.
        const headers = await authHeaders()
        let response: Response
        let data: any

        if (preview) {
            const rowsUrl = `${API_URL}/people/search?page=${page}&page_size=${limit}&preview=true`
            const countUrl = `${API_URL}/people/count`
            const [rowsRes, countRes] = await Promise.all([
                fetch(rowsUrl, { method: "POST", headers, body: JSON.stringify(requestBody), signal }),
                fetch(countUrl, { method: "POST", headers, body: JSON.stringify(requestBody), signal }),
            ])
            if (!rowsRes.ok) {
                throw new Error(`Search failed: ${rowsRes.status} - ${await rowsRes.text()}`)
            }
            const rowsData = await rowsRes.json()
            const countData = countRes.ok ? await countRes.json() : { count: 0 }
            // Mirror the response shape the downstream code expects: rows under
            // `data`, total/company_ids_count/total_companies_matched at top
            // level for the extrapolation calc below.
            data = {
                ...rowsData,
                total: typeof countData.count === "number" ? countData.count : 0,
                company_ids_count: countData.company_ids_count ?? rowsData.company_ids_count,
                total_companies_matched: countData.total_companies_matched ?? rowsData.total_companies_matched,
            }
            response = rowsRes
        } else {
            response = await fetch(`${API_URL}/people/search?page=${page}&page_size=${limit}`, {
                method: "POST",
                headers,
                body: JSON.stringify(requestBody),
                signal
            })

            if (!response.ok) {
                const errorText = await response.text()
                throw new Error(`Search failed: ${response.status} - ${errorText}`)
            }

            data = await response.json()
        }
        console.log("[api.ts] searchPeople response (preview=" + preview + "):", data)

        // Transform backend response to frontend format
        const people: PersonData[] = data.data.map((person: any) => ({
            id: person.id || "",
            first_name: person.first_name || "",
            last_name: person.last_name || "",
            title: person.title || person.headline || "",
            seniority: person.seniority || undefined,
            linkedin_url: person.linkedin_url || "",
            city: person.city || undefined,
            state: person.state || undefined,
            country: person.country || undefined,
            formatted_address: [person.city, person.state, person.country].filter(Boolean).join(", ") || undefined,
            organization: person.company_name
                ? {
                      name: person.company_name
                  }
                : undefined,
            company_linkedin_id: person.company_linkedin_id || undefined,
            schools: person.education?.map((e: any) => e.school_name).filter(Boolean) || []
        }))

        return {
            people,
            totalResults: data.total,
            totalPages: data.total_pages,
            currentPage: data.page,
            companyIdsCount: data.company_ids_count,
            totalCompaniesMatched: data.total_companies_matched,
            sessionId: `session-${Date.now()}`
        }
    } catch (error) {
        // Aborts are expected (caller cancelled — e.g. filter changed mid-fetch).
        // Logging them surfaces a Console AbortError in Next.js dev overlay
        // even though the caller handles it; suppress and re-throw silently.
        if ((error as { name?: string } | null)?.name !== "AbortError") {
            console.error("[api.ts] searchPeople error:", error)
        }
        throw error
    }
}

/**
 * Fetch people directly from Supabase for given people list IDs, with in-memory filtering.
 * Bypasses OpenSearch URL-matching — returns exactly who is in the list, filtered by people filters.
 */
export async function fetchPeopleFromLists(params: {
    listIds: number[]
    domain: string
    page?: number
    pageSize?: number
    filters?: PeopleFilters
}): Promise<SearchPeopleResult> {
    const { listIds, domain, page = 1, pageSize = 25, filters } = params
    console.log("[api.ts] fetchPeopleFromLists called with:", { listIds, domain, page, pageSize })

    // Build company filters for server-side resolution via OpenSearch
    const hasCompanyFilters = !!(
        filters?.company_stage?.length ||
        filters?.company_investors?.length ||
        filters?.company_industries?.length ||
        filters?.company_linkedin_industries?.length ||
        filters?.company_employee_counts?.length ||
        filters?.company_revenue_ranges?.length
    )

    const body: Record<string, any> = {
        list_ids: listIds,
        domain,
        page,
        page_size: pageSize,
        // Only pass simple string/ID filters. Skip location/country (default "USA"
        // would filter everyone out).
        person_titles: filters?.person_titles || [],
        person_title_boost: filters?.person_title_boost || [],
        person_title_boost_primary: filters?.person_title_boost_primary || [],
        person_title_boost_secondary: filters?.person_title_boost_secondary || [],
        person_seniorities: filters?.person_seniorities || [],
        // Pass company-level filters so backend can resolve matching companies via OpenSearch
        ...(hasCompanyFilters && {
            company_filters: {
                fundingRounds: filters?.company_stage || [],
                investors: filters?.company_investors || [],
                crunchbaseIndustries: filters?.company_industries || [],
                linkedinIndustries: filters?.company_linkedin_industries || [],
                employeeCounts: filters?.company_employee_counts || [],
                revenueRanges: filters?.company_revenue_ranges || [],
            }
        }),
    }

    const response = await fetch(`${API_URL}/lists/people-list-members`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify(body),
    })

    if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`fetchPeopleFromLists failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log("[api.ts] fetchPeopleFromLists response:", data)

    const people: PersonData[] = data.data.map((person: any) => ({
        id: person.id || "",
        first_name: person.first_name || "",
        last_name: person.last_name || "",
        title: person.title || person.headline || "",
        seniority: undefined,
        linkedin_url: person.linkedin_url || "",
        city: person.city || undefined,
        state: person.state || undefined,
        country: person.country || undefined,
        formatted_address: [person.city, person.state, person.country].filter(Boolean).join(", ") || undefined,
        organization: person.company_name ? { name: person.company_name } : undefined,
        company_linkedin_id: person.company_linkedin_id || undefined,
        schools: []
    }))

    return {
        people,
        totalResults: data.total,
        totalPages: data.total_pages,
        currentPage: data.page,
        sessionId: `session-${Date.now()}`
    }
}

// ============================================
// AI FILTER FUNCTIONS
// ============================================

export interface AutoSaveConfig {
    list_name?: string
    existing_list_id?: number
    org_domain: string
    owner_email?: string
    company_filters?: Record<string, any>
    people_filters?: Record<string, any>
    ai_filters?: Record<string, any>[]
    sales_nav_url?: string
    exclude_linkedin_urls?: string[]
}

export interface StartAIFilterParams {
    sessionId: string
    domain?: string
    companyFilters: CompanyFilters
    peopleFilters: PeopleFilters
    aiFilters: AIFilter[]
    matchLimit?: number // Stop after N matches (0 = no limit)
    perCompanyLimit?: number // Stop matching from a company after N matches (0 = no limit)
    excludeLinkedInUrls?: string[]
    autoSaveConfig?: AutoSaveConfig
}

export interface StartAIFilterResult {
    sessionId: string
    total: number
    status: string
    message?: string
}

export interface AIFilterProgress {
    sessionId: string
    total: number
    analyzed: number
    matches: number
    matchLimit?: number
    isComplete: boolean
    status: string
    results: AIFilterResult[]
}

export interface AIFilterResult {
    id: number
    person_id: string
    first_name: string | null
    last_name: string | null
    title: string | null
    company_name: string | null
    company_linkedin_id: string | null
    linkedin_url: string | null
    location: string | null
    company_url: string | null
    ai_analyzed: boolean
    ai_is_match: boolean | null
    ai_filter_results: Record<string, unknown> | null
}

// Legacy interface for backwards compatibility
export interface RunAIFiltersParams {
    sessionId: string
    aiFilters: AIFilter[]
    limit?: number
    perCompanyLimit?: number
    mode: "companies" | "people"
}

/**
 * Start AI filter processing for people search
 * This queries all matching people and inserts them into the DB queue
 */
export async function startAIFilter(params: StartAIFilterParams): Promise<StartAIFilterResult> {
    console.log("[api.ts] startAIFilter called with:", params)
    console.log("[api.ts] AI Filter - Excluding", params.excludeLinkedInUrls?.length || 0, "LinkedIn URLs")

    try {
        // Expand regions to their constituent countries (include + exclude)
        const expandedCountries = await expandRegionsToCountries(params.peopleFilters.person_countries || [])
        const expandedExcludeCountries = await expandRegionsToCountries(params.peopleFilters.exclude_person_countries || [])

        // Merge state filters into location arrays (same as searchPeople)
        const mergedCompanyLocations = [...(params.peopleFilters.company_locations || []), ...(params.peopleFilters.company_states || [])]
        const mergedPersonLocations = [...(params.peopleFilters.person_locations || []), ...(params.peopleFilters.person_states || [])]

        // IMPORTANT: Extract company filters from peopleFilters (same as searchPeople does)
        // The companyFilters param is for company-mode search, but for people search
        // the company filters are embedded in peopleFilters.company_* fields
        const backendCompanyFilters = {
            fundingRounds: params.peopleFilters.company_stage || [],
            investors: params.peopleFilters.company_investors || [],
            fiberIndustries: [],
            crunchbaseIndustries: params.peopleFilters.company_industries || [],
            linkedinIndustries: params.peopleFilters.company_linkedin_industries || [],
            employeeCounts: params.peopleFilters.company_employee_counts || [],
            revenueRanges: params.peopleFilters.company_revenue_ranges || [],
            company_location_filters: mergedCompanyLocations,
            foundedYears: [],
            filterMode: params.peopleFilters.company_filter_mode || "venture",
            include_companies: params.peopleFilters.current_companies || [],
            exclude_list_ids: params.peopleFilters.excludeCompanyListIds || [],
            naics_codes: params.peopleFilters.company_naics_codes || [],
            accelerators: params.peopleFilters.company_accelerators || [],
            keywords: params.peopleFilters.company_keywords || [],
            company_keyword_boost: params.peopleFilters.company_keyword_boost || [],
            exclude_crunchbase_industries: params.peopleFilters.exclude_company_industries || [],
            exclude_linkedin_industries: params.peopleFilters.exclude_company_linkedin_industries || []
        }

        const backendPeopleFilters = {
            person_countries: expandedCountries,
            person_locations: mergedPersonLocations,
            person_titles: params.peopleFilters.person_titles || [],
            person_title_boost: params.peopleFilters.person_title_boost || [],
            person_title_boost_primary: params.peopleFilters.person_title_boost_primary || [],
            person_title_boost_secondary: params.peopleFilters.person_title_boost_secondary || [],
            person_seniorities: params.peopleFilters.person_seniorities || [],
            person_functions: [],
            alumni_schools: params.peopleFilters.alumni_schools || [],
            fields_of_study: params.peopleFilters.fields_of_study || [],
            years_out_of_college: params.peopleFilters.years_out_of_college || [],
            years_at_current_company: params.peopleFilters.years_at_current_company || [],
            linkedin_summary_keywords: params.peopleFilters.linkedin_summary_keywords || [],
            min_linkedin_connections: params.peopleFilters.min_linkedin_connections,
            contact_email_status: [],
            current_companies: params.peopleFilters.current_companies || [],
            previous_companies: params.peopleFilters.previous_companies || [],
            company_location_filters: mergedCompanyLocations,
            company_employee_counts: params.peopleFilters.company_employee_counts || [],
            company_revenue_ranges: params.peopleFilters.company_revenue_ranges || [],
            company_stage: params.peopleFilters.company_stage || [],
            company_investors: params.peopleFilters.company_investors || [],
            company_industries: params.peopleFilters.company_industries || [],
            company_fiber_industries: [],
            company_linkedin_industries: params.peopleFilters.company_linkedin_industries || [],
            company_accelerators: params.peopleFilters.company_accelerators || [],
            company_filter_mode: params.peopleFilters.company_filter_mode || "venture",
            company_keywords: params.peopleFilters.company_keywords || [],
            company_keyword_boost: params.peopleFilters.company_keyword_boost || [],
            per_company_limit: null,
            exclude_company_list_ids: params.peopleFilters.excludeCompanyListIds || [],
            exclude_linkedin_urls: params.excludeLinkedInUrls || [],
            exclude_person_titles: params.peopleFilters.exclude_person_titles || [],
            exclude_person_countries: expandedExcludeCountries,
            exclude_company_industries: params.peopleFilters.exclude_company_industries || [],
            exclude_company_linkedin_industries: params.peopleFilters.exclude_company_linkedin_industries || []
        }

        const backendAIFilters = params.aiFilters.map(f => ({
            question: f.text,
            use_website_scrape: f.useWebsiteScrape || false,
            use_google_search: f.useGoogleSearch || false,
            use_employee_info: f.useEmployeeInfo || false,
            use_linkedin_employees: f.useLinkedInEmployees || false,
            use_job_postings: f.useJobPostings || false,
            use_linkedin_posts: f.useLinkedInPosts || false,
            use_tech_stack: f.useTechStack || false,
            use_linkedin_company: f.useLinkedInCompany || false,
            use_screenshot: f.useScreenshot || false
        }))

        const response = await fetch(`/api/leads-v2/ai-filter/start`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({
                session_id: params.sessionId,
                domain: params.domain || "default",
                company_filters: backendCompanyFilters,
                people_filters: backendPeopleFilters,
                ai_filters: backendAIFilters,
                match_limit: params.matchLimit || 0, // 0 = no limit
                per_company_limit: params.perCompanyLimit || 0, // 0 = no per-company limit
                auto_save_config: params.autoSaveConfig || null
            })
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`AI filter start failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] startAIFilter response:", data)

        return {
            sessionId: data.session_id,
            total: data.total,
            status: data.status,
            message: data.message
        }
    } catch (error) {
        console.error("[api.ts] startAIFilter error:", error)
        throw error
    }
}

/**
 * Start AI filter for imported people (Sales Nav, etc.)
 * This skips OpenSearch and directly processes the provided people list.
 */
export interface StartImportedAIFilterParams {
    sessionId: string
    domain?: string
    people: PersonData[]
    aiFilters: AIFilter[]
    matchLimit?: number
}

export async function startImportedAIFilter(params: StartImportedAIFilterParams): Promise<StartAIFilterResult> {
    console.log("[api.ts] startImportedAIFilter called with:", params.sessionId, "people:", params.people.length)

    try {
        const backendAIFilters = params.aiFilters.map(f => ({
            question: f.text,
            use_website_scrape: f.useWebsiteScrape || false,
            use_google_search: f.useGoogleSearch || false,
            use_employee_info: f.useEmployeeInfo || false,
            use_linkedin_employees: f.useLinkedInEmployees || false,
            use_job_postings: f.useJobPostings || false,
            use_linkedin_posts: f.useLinkedInPosts || false,
            use_tech_stack: f.useTechStack || false,
            use_linkedin_company: f.useLinkedInCompany || false,
            use_screenshot: f.useScreenshot || false
        }))

        const backendPeople = params.people.map(p => ({
            linkedin_url: p.linkedin_url,
            first_name: p.first_name,
            last_name: p.last_name,
            title: p.title,
            headline: p.title, // Use title as headline fallback
            company_name: p.organization?.name,
            company_linkedin_id: p.company_linkedin_id,
            formatted_address: p.formatted_address,
            organization: p.organization
        }))

        const response = await fetch(`/api/leads-v2/ai-filter/start-imported`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({
                session_id: params.sessionId,
                domain: params.domain || "default",
                people: backendPeople,
                ai_filters: backendAIFilters,
                match_limit: params.matchLimit || 0
            })
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`AI filter start-imported failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] startImportedAIFilter response:", data)

        return {
            sessionId: data.session_id,
            total: data.total,
            status: data.status,
            message: data.message
        }
    } catch (error) {
        console.error("[api.ts] startImportedAIFilter error:", error)
        throw error
    }
}

/**
 * Get AI filter progress and results (for polling)
 * Supports pagination via page and pageSize params
 */
export async function getAIFilterProgress(sessionId: string, page: number = 1, pageSize: number = 25): Promise<AIFilterProgress> {
    console.log("[api.ts] getAIFilterProgress called for:", sessionId, "page:", page)

    try {
        const response = await fetch(buildAiFilterProgressUrl(sessionId, "people", page, pageSize), {
            headers: await authHeaders(),
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`AI filter progress failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] getAIFilterProgress response:", data)

        return {
            sessionId: data.session_id,
            total: data.total,
            analyzed: data.analyzed,
            matches: data.matches,
            matchLimit: data.match_limit || undefined,
            isComplete: data.is_complete,
            status: data.status,
            results: data.results
        }
    } catch (error) {
        console.error("[api.ts] getAIFilterProgress error:", error)
        throw error
    }
}

/**
 * Extend an existing AI filter session: bump match_limit and resume the worker
 * so "add N more" lands as a contiguous net-new N matches in the SAME session
 * (no new tool_use, no new card, no list duplication). Resume re-enqueues
 * `ai_analyzed=false` rows from the existing candidate pool; if the pool is
 * exhausted before the new cap, the session completes early — caller should
 * check `status` in the response. Save-side cap (`save_service.py:670–752`)
 * reads the bumped `ai_match_limit` per-call, so the bound list grows cleanly.
 */
export async function extendAIFilter(params: {
    sessionId: string
    additionalMatches: number
}): Promise<{
    sessionId: string
    aiMatchLimit: number
    aiRunId: string
    status: string
    pending: number
    alreadyAnalyzed: number
}> {
    const response = await fetch(`${API_URL}/ai-filter/extend`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
            session_id: params.sessionId,
            additional_matches: params.additionalMatches,
        }),
    })
    if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`AI filter extend failed: ${response.status} - ${errorText}`)
    }
    const data = await response.json()
    return {
        sessionId: data.session_id,
        aiMatchLimit: data.ai_match_limit,
        aiRunId: data.ai_run_id,
        status: data.status,
        pending: data.pending,
        alreadyAnalyzed: data.already_analyzed,
    }
}

/**
 * Cancel AI filter processing
 */
export async function cancelAIFilters(sessionId: string): Promise<void> {
    console.log("[api.ts] cancelAIFilters called for:", sessionId)

    try {
        const response = await fetch(`/api/leads-v2/ai-filter/cancel?session_id=${sessionId}`, {
            method: "POST",
            headers: await authHeaders()
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`AI filter cancel failed: ${response.status} - ${errorText}`)
        }

        console.log("[api.ts] cancelAIFilters success")
    } catch (error) {
        console.error("[api.ts] cancelAIFilters error:", error)
        throw error
    }
}

/**
 * Legacy function - wraps startAIFilter for backwards compatibility
 */
export async function runAIFilters(params: RunAIFiltersParams): Promise<void> {
    console.log("[api.ts] runAIFilters called - redirecting to startAIFilter")
    // This is a simplified wrapper - actual implementation needs filters from state
    // The component should call startAIFilter directly with full params
}

// ============================================
// COMPANY AI FILTER FUNCTIONS
// ============================================

export interface StartAIFilterCompaniesParams {
    sessionId: string
    companyFilters: CompanyFilters
    aiFilters: AIFilter[]
    matchLimit?: number
    autoSaveConfig?: AutoSaveConfig
}

/**
 * Start AI filter processing for company search
 */
export async function startAIFilterCompanies(params: StartAIFilterCompaniesParams): Promise<StartAIFilterResult> {
    console.log("[api.ts] startAIFilterCompanies called with:", params)

    try {
        const { normalizeCompanyFilters } = await import("@/app/(app)/agent/AgentFiltersModal")
        const companyFilters = normalizeCompanyFilters(params.companyFilters as unknown as Record<string, unknown>)

        // Expand regions (e.g. European Union) to their constituent countries — must match searchCompanies
        const expandedCountries = await expandRegionsToCountries(companyFilters.company_countries || [])
        const expandedExcludeCountries = await expandRegionsToCountries(companyFilters.excludeCompanyCountries || [])

        // Transform frontend filters to backend format (must match searchCompanies)
        const backendCompanyFilters = {
            fundingRounds: companyFilters.fundingRounds || [],
            investors: companyFilters.investors || [],
            fiberIndustries: [],
            crunchbaseIndustries: companyFilters.crunchbaseIndustries || [],
            linkedinIndustries: companyFilters.linkedinIndustries || [],
            employeeCounts: companyFilters.employeeCounts || [],
            revenueRanges: companyFilters.revenueRanges || [],
            company_countries: expandedCountries,
            company_location_filters: [...(companyFilters.company_locations || []), ...(companyFilters.company_states || [])],
            foundedYears: companyFilters.foundedYears || [],
            filterMode: companyFilters.filterMode || "venture",
            include_companies: companyFilters.include_companies || [],
            exclude_list_ids: companyFilters.excludeListIds || [],
            naics_codes: companyFilters.naicsCodes || [],
            keywords: companyFilters.keywords || [],
            companyKeywordBoost: companyFilters.companyKeywordBoost || [],
            accelerators: companyFilters.accelerators || [],
            exclude_crunchbase_industries: companyFilters.excludeCrunchbaseIndustries || [],
            exclude_linkedin_industries: companyFilters.excludeLinkedinIndustries || [],
            exclude_company_countries: expandedExcludeCountries
        }

        const backendAIFilters = params.aiFilters.map(f => ({
            question: f.text,
            use_website_scrape: f.useWebsiteScrape || false,
            use_google_search: f.useGoogleSearch || false,
            use_linkedin_employees: f.useLinkedInEmployees || false,
            use_job_postings: f.useJobPostings || false,
            use_tech_stack: f.useTechStack || false,
            use_linkedin_company: f.useLinkedInCompany || false,
            use_screenshot: f.useScreenshot || false
        }))

        const response = await fetch(`/api/leads-v2/ai-filter/company/start`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({
                session_id: params.sessionId,
                company_filters: backendCompanyFilters,
                ai_filters: backendAIFilters,
                match_limit: params.matchLimit || 0,
                auto_save_config: params.autoSaveConfig || null
            })
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Company AI filter start failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] startAIFilterCompanies response:", data)

        return {
            sessionId: data.session_id,
            total: data.total,
            status: data.status,
            message: data.message
        }
    } catch (error) {
        console.error("[api.ts] startAIFilterCompanies error:", error)
        throw error
    }
}

/**
 * Get company AI filter progress and results
 * Supports pagination via page and pageSize params
 */
export async function getAIFilterProgressCompanies(sessionId: string, page: number = 1, pageSize: number = 25): Promise<AIFilterProgress> {
    console.log("[api.ts] getAIFilterProgressCompanies called for:", sessionId, "page:", page)

    try {
        const response = await fetch(buildAiFilterProgressUrl(sessionId, "companies", page, pageSize), {
            headers: await authHeaders(),
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Company AI filter progress failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] getAIFilterProgressCompanies response:", data)

        return {
            sessionId: data.session_id,
            total: data.total,
            analyzed: data.analyzed,
            matches: data.matches,
            matchLimit: data.match_limit || undefined,
            isComplete: data.is_complete,
            status: data.status,
            results: data.results
        }
    } catch (error) {
        console.error("[api.ts] getAIFilterProgressCompanies error:", error)
        throw error
    }
}

/**
 * Cancel company AI filter processing
 */
export async function cancelAIFiltersCompanies(sessionId: string): Promise<void> {
    console.log("[api.ts] cancelAIFiltersCompanies called for:", sessionId)

    try {
        const response = await fetch(`/api/leads-v2/ai-filter/company/cancel?session_id=${sessionId}`, {
            method: "POST",
            headers: await authHeaders()
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Company AI filter cancel failed: ${response.status} - ${errorText}`)
        }

        console.log("[api.ts] cancelAIFiltersCompanies success")
    } catch (error) {
        console.error("[api.ts] cancelAIFiltersCompanies error:", error)
        throw error
    }
}

// ============================================
// SAVE/EXPORT FUNCTIONS
// ============================================

export interface SaveToListParams {
    sessionId: string
    listName?: string  // Required for new list, optional for existing
    existingListId?: number  // If provided, append to this list instead of creating new
    mode: "companies" | "people"
    saveAIMatchesOnly?: boolean
    orgDomain?: string
    ownerEmail?: string
    companyFilters?: CompanyFilters
    peopleFilters?: PeopleFilters
    excludeLinkedInUrls?: string[]
    sourceListIds?: number[]
    aiFilters?: {
        question: string
        use_job_postings: boolean
        use_employee_info: boolean
        use_google_search: boolean
        use_website_scrape: boolean
        use_linkedin_employees: boolean
        use_linkedin_posts: boolean
        use_tech_stack: boolean
        use_screenshot: boolean
        use_linkedin_company: boolean
    }[]
    salesNavUrl?: string
    /** Cap the OpenSearch result set saved into the list. Honors the
     *  Confirm-filters card's match-limit picker (50 / 100 / Custom) on
     *  the structured-only Save path that doesn't go through AI scoring.
     *  0 / undefined = no cap. */
    matchLimit?: number
    /** Tier threshold for which leads to save based on AI filter match ratio.
     *  "A" = all criteria, "AB" = 2/3+, "ABC" = 1/3+, "all" = everything analyzed. */
    saveTier?: "A" | "AB" | "ABC"
    /** Agent chat that owns this save. Drives the singleton-binding check on
     *  the server: each agent_chats row owns AT MOST one lead_list. When
     *  provided AND the chat is already bound to a DIFFERENT list, the
     *  server returns HTTP 409 with `code: "chat_list_binding_violation"`.
     *  Omit on saves that aren't tied to an agent session (lists page CSV
     *  upload, dataset imports, etc.) — those don't participate. */
    agentChatId?: string
    signal?: AbortSignal
}

/**
 * Save search results to a new list
 * - If saveAIMatchesOnly: saves AI matches from Supabase
 * - Otherwise: runs full OpenSearch query and saves all results
 */
export async function saveToList(params: SaveToListParams): Promise<{ listId: number; savedCount: number; blockedCount?: number; dedupCount?: number; skippedCount?: number }> {
    console.log("[api.ts] saveToList called with:", params)
    console.log("[api.ts] Save - Excluding", params.excludeLinkedInUrls?.length || 0, "LinkedIn URLs")

    try {
        // Expand regions to their constituent countries
        const expandedCountries = params.peopleFilters ? await expandRegionsToCountries(params.peopleFilters.person_countries || []) : []

        // Build backend filters based on mode
        let backendCompanyFilters: Record<string, any> = {}

        if (params.mode === "companies" && params.companyFilters) {
            // For company mode, use companyFilters directly
            backendCompanyFilters = {
                fundingRounds: params.companyFilters.fundingRounds || [],
                investors: params.companyFilters.investors || [],
                fiberIndustries: [],
                crunchbaseIndustries: params.companyFilters.crunchbaseIndustries || [],
                linkedinIndustries: params.companyFilters.linkedinIndustries || [],
                employeeCounts: params.companyFilters.employeeCounts || [],
                revenueRanges: params.companyFilters.revenueRanges || [],
                company_countries: params.companyFilters.company_countries || [],
                company_location_filters: params.companyFilters.company_locations || [],
                foundedYears: params.companyFilters.foundedYears || [],
                filterMode: params.companyFilters.filterMode || "venture",
                include_companies: params.companyFilters.include_companies || [],
                exclude_list_ids: params.companyFilters.excludeListIds || [],
                naics_codes: params.companyFilters.naicsCodes || [],
                keywords: params.companyFilters.keywords || []
            }
        } else if (params.peopleFilters) {
            // For people mode, extract company filters from peopleFilters
            backendCompanyFilters = {
                fundingRounds: params.peopleFilters.company_stage || [],
                investors: params.peopleFilters.company_investors || [],
                fiberIndustries: [],
                crunchbaseIndustries: params.peopleFilters.company_industries || [],
                linkedinIndustries: params.peopleFilters.company_linkedin_industries || [],
                employeeCounts: params.peopleFilters.company_employee_counts || [],
                revenueRanges: params.peopleFilters.company_revenue_ranges || [],
                company_location_filters: params.peopleFilters.company_locations || [],
                foundedYears: [],
                filterMode: params.peopleFilters.company_filter_mode || "venture",
                include_companies: params.peopleFilters.current_companies || [],
                exclude_list_ids: params.peopleFilters.excludeCompanyListIds || [],
                naics_codes: params.peopleFilters.company_naics_codes || [],
                accelerators: params.peopleFilters.company_accelerators || []
            }
        }

        const backendPeopleFilters = params.peopleFilters
            ? {
                  person_countries: expandedCountries, // Use expanded countries
                  person_locations: params.peopleFilters.person_locations || [],
                  person_titles: params.peopleFilters.person_titles || [],
                  person_title_boost: params.peopleFilters.person_title_boost || [],
                  person_title_boost_primary: params.peopleFilters.person_title_boost_primary || [],
                  person_title_boost_secondary: params.peopleFilters.person_title_boost_secondary || [],
                  person_seniorities: params.peopleFilters.person_seniorities || [],
                  person_functions: [],
                  alumni_schools: params.peopleFilters.alumni_schools || [],
                  fields_of_study: params.peopleFilters.fields_of_study || [],
                  years_out_of_college: params.peopleFilters.years_out_of_college || [],
                  years_at_current_company: params.peopleFilters.years_at_current_company || [],
                  linkedin_summary_keywords: params.peopleFilters.linkedin_summary_keywords || [],
                  min_linkedin_connections: params.peopleFilters.min_linkedin_connections,
                  contact_email_status: [],
                  current_companies: params.peopleFilters.current_companies || [],
                  company_location_filters: params.peopleFilters.company_locations || [],
                  company_employee_counts: params.peopleFilters.company_employee_counts || [],
                  company_revenue_ranges: params.peopleFilters.company_revenue_ranges || [],
                  company_stage: params.peopleFilters.company_stage || [],
                  company_investors: params.peopleFilters.company_investors || [],
                  company_industries: params.peopleFilters.company_industries || [],
                  company_fiber_industries: [],
                  company_linkedin_industries: params.peopleFilters.company_linkedin_industries || [],
                  company_accelerators: params.peopleFilters.company_accelerators || [],
                  company_filter_mode: params.peopleFilters.company_filter_mode || "venture",
                  per_company_limit: null,
                  exclude_company_list_ids: params.peopleFilters.excludeCompanyListIds || [],
                  exclude_linkedin_urls: params.excludeLinkedInUrls || []
              }
            : {}

        const response = await fetch(`${API_URL}/save-to-list`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({
                session_id: params.sessionId,
                list_name: params.listName,
                existing_list_id: params.existingListId,
                mode: params.mode,
                save_ai_matches_only: params.saveAIMatchesOnly || false,
                org_domain: params.orgDomain,
                owner_email: params.ownerEmail || null,
                company_filters: backendCompanyFilters,
                people_filters: backendPeopleFilters,
                ai_filters: params.aiFilters || null,
                sales_nav_url: params.salesNavUrl || null,
                source_list_ids: params.sourceListIds || null,
                match_limit: params.matchLimit && params.matchLimit > 0 ? Math.floor(params.matchLimit) : 0,
                save_tier: params.saveTier || "A",
                agent_chat_id: params.agentChatId || null,
            }),
            signal: params.signal
        })

        if (!response.ok) {
            const errorText = await response.text()
            // Singleton-binding violation: surface the typed shape so callers
            // can recognize and render a chat-specific error instead of a
            // generic "Save failed". The server returns 409 with a JSON body
            // shaped { code: "chat_list_binding_violation", bound_list_id, ... }
            // wrapped in FastAPI's { detail: ... } envelope.
            if (response.status === 409) {
                let parsed: { detail?: { code?: string; bound_list_id?: number; message?: string } } = {}
                try { parsed = JSON.parse(errorText) } catch { /* fall through */ }
                const code = parsed?.detail?.code
                if (code === "chat_list_binding_violation") {
                    const err: Error & {
                        code?: string; status?: number; boundListId?: number
                    } = new Error(parsed?.detail?.message || "This chat is already linked to a different list.")
                    err.code = code
                    err.status = 409
                    err.boundListId = parsed?.detail?.bound_list_id
                    throw err
                }
            }
            // Transient connection drop on the server (httpx
            // "Server disconnected" from Supabase). Server returns 503
            // with code "transient_save_error"; surface it as a typed
            // Error so the autosave hook can retry quietly on the next
            // match-tick instead of latching a red "Save failed" pill.
            if (response.status === 503) {
                let parsed: { detail?: { code?: string; message?: string } } = {}
                try { parsed = JSON.parse(errorText) } catch { /* fall through */ }
                const code = parsed?.detail?.code
                if (code === "transient_save_error") {
                    const err: Error & { code?: string; status?: number; transient?: boolean } = new Error(
                        parsed?.detail?.message || "Temporary connection issue. The save will retry automatically.",
                    )
                    err.code = code
                    err.status = 503
                    err.transient = true
                    throw err
                }
            }
            throw new Error(`Save failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] saveToList response:", data)

        return {
            listId: data.list_id,
            savedCount: data.saved_count,
            blockedCount: data.blocked_count ?? undefined,
            dedupCount: data.dedup_count ?? undefined,
            skippedCount: data.skipped_count ?? undefined,
        }
    } catch (error) {
        const name = (error as { name?: string } | null)?.name
        const transient = (error as { transient?: boolean } | null)?.transient === true
        // Aborts are expected (autosave cancels on session change).
        // Transient 503s are retried by the caller on the next match-tick —
        // log as warn so they stay out of Next.js's dev-mode error overlay.
        if (name === "AbortError") {
            // swallow
        } else if (transient) {
            console.warn("[api.ts] saveToList transient error (will retry):", error)
        } else {
            console.error("[api.ts] saveToList error:", error)
        }
        throw error
    }
}

/**
 * Cancel an in-progress save operation
 */
export async function cancelSaveToList(sessionId: string): Promise<void> {
    console.log("[api.ts] cancelSaveToList called for session:", sessionId)
    try {
        await fetch(`${API_URL}/save-to-list/cancel?session_id=${sessionId}`, {
            method: "POST",
            headers: await authHeaders()
        })
    } catch (error) {
        console.error("[api.ts] cancelSaveToList error:", error)
    }
}

export interface AppendToListParams {
    sessionId: string
    listId: number
    mode: "companies" | "people"
    aiMatchesOnly?: boolean
}

/**
 * Append search results to an existing list
 */
export async function appendToList(params: AppendToListParams): Promise<void> {
    console.log("[api.ts] appendToList called with:", params)
    // TODO: Implement actual API call
}

/**
 * Export search results to CSV
 */
export async function exportToCSV(sessionId: string, mode: "companies" | "people", aiMatchesOnly?: boolean): Promise<Blob> {
    console.log("[api.ts] exportToCSV called for:", sessionId, mode)
    // TODO: Implement actual API call
    return new Blob([""], { type: "text/csv" })
}

// ============================================
// LIST MANAGEMENT
// ============================================

/**
 * Get available company lists for exclusion
 */
export async function getCompanyLists(): Promise<{ id: number; name: string; company_count: number }[]> {
    console.log("[api.ts] getCompanyLists called")
    // TODO: Implement actual API call
    return []
}

/**
 * Get available people lists for exclusion
 */
export async function getPeopleLists(): Promise<{ id: number; name: string; people_count: number }[]> {
    console.log("[api.ts] getPeopleLists called")
    // TODO: Implement actual API call
    return []
}

// ============================================
// IMPORT COMPANY LIST FUNCTIONS
// ============================================

export interface CompanyListItem {
    id: number
    name: string
    created_at: string
    company_count: number
}

export interface CompanyListLinkedInIds {
    linkedin_ids: string[]
    company_count: number
    companies: {
        id: number
        name: string | null
        website: string | null
        linkedin_ids: string[]
        li_org_id: string | null  // Primary LinkedIn org ID (positive ID preferred)
    }[]
}

/**
 * Get all company lists (type='company') for importing
 */
export async function getCompanyListsForImport(domain: string): Promise<CompanyListItem[]> {
    console.log("[api.ts] getCompanyListsForImport called for domain:", domain)

    try {
        const response = await fetch(`${API_URL}/lists/company-lists?domain=${encodeURIComponent(domain)}`, {
            headers: await authHeaders()
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to get company lists: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] getCompanyListsForImport response:", data)

        return data.lists || []
    } catch (error) {
        console.error("[api.ts] getCompanyListsForImport error:", error)
        throw error
    }
}

/**
 * Get LinkedIn IDs from a company list
 * These can be used to search for people at those companies
 */
export async function getCompanyListLinkedInIds(listId: number): Promise<CompanyListLinkedInIds> {
    console.log("[api.ts] getCompanyListLinkedInIds called for list:", listId)

    try {
        const response = await fetch(`${API_URL}/lists/company-list-linkedin-ids?list_id=${listId}`, {
            headers: await authHeaders()
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to get LinkedIn IDs: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] getCompanyListLinkedInIds response:", data)

        return data
    } catch (error) {
        console.error("[api.ts] getCompanyListLinkedInIds error:", error)
        throw error
    }
}

// ============================================
// IMPORT FUNCTIONS
// ============================================

export interface ImportByDomainsParams {
    domains: string[]
    mode: "companies" | "people"
    peopleFilters?: PeopleFilters // Only for people mode
}

/**
 * Import companies or find people by domain list
 * Uses the same underlying search functions
 */
export async function importByDomains(params: ImportByDomainsParams): Promise<SearchCompaniesResult | SearchPeopleResult> {
    console.log("[api.ts] importByDomains called with:", params)

    if (params.mode === "companies") {
        // Convert domains to company search
        return searchCompanies({
            filters: {
                filterMode: "venture"
                // TODO: Add domain filter support
            }
        })
    } else {
        // Convert domains to people search
        return searchPeople({
            filters: params.peopleFilters || {
                company_filter_mode: "venture"
            }
            // TODO: Add domain filter support
        })
    }
}

// ============================================
// AUTOCOMPLETE FUNCTIONS
// ============================================

/**
 * Search for companies by name (for autocomplete)
 */
export async function searchCompanyNames(query: string): Promise<{ name: string; domain: string }[]> {
    console.log("[api.ts] searchCompanyNames called with:", query)
    // TODO: Implement actual API call
    return []
}

/**
 * Search for investors (for autocomplete)
 */
export async function searchInvestors(query: string): Promise<string[]> {
    console.log("[api.ts] searchInvestors called with:", query)
    // TODO: Implement actual API call
    return []
}

/**
 * Search for schools (for autocomplete)
 */
export async function searchSchools(query: string): Promise<{ school_id: string; school_name: string }[]> {
    console.log("[api.ts] searchSchools called with:", query)
    // TODO: Implement actual API call
    return []
}

/**
 * Search for locations (for autocomplete)
 */
export async function searchLocations(query: string): Promise<
    {
        display_name: string
        city: string | null
        state: string | null
        country: string | null
        type: "city" | "state" | "country"
    }[]
> {
    console.log("[api.ts] searchLocations called with:", query)
    // TODO: Implement actual API call
    return []
}

/**
 * Suggest related job titles based on user input and current search context
 */
export interface TitleSuggestion {
    title: string
    count: number
    reason: string
}

export async function suggestTitles(params: {
    peopleFilters: PeopleFilters
    companyFilters?: CompanyFilters
    inputTitle: string
}): Promise<TitleSuggestion[]> {
    console.log("[api.ts] suggestTitles called with:", params.inputTitle)

    try {
        const filters = params.peopleFilters

        // Expand regions to their constituent countries
        const expandedCountries = await expandRegionsToCountries(filters.person_countries || [])

        // Build company filters from people filters (same as searchPeople)
        const companyFilters = {
            fundingRounds: filters.company_stage || [],
            investors: filters.company_investors || [],
            fiberIndustries: [],
            crunchbaseIndustries: filters.company_industries || [],
            linkedinIndustries: filters.company_linkedin_industries || [],
            employeeCounts: filters.company_employee_counts || [],
            revenueRanges: filters.company_revenue_ranges || [],
            company_location_filters: filters.company_locations || [],
            foundedYears: [],
            filterMode: filters.company_filter_mode || "venture",
            include_companies: filters.current_companies || [],
            exclude_list_ids: filters.excludeCompanyListIds || [],
            naics_codes: filters.company_naics_codes || [],
            accelerators: filters.company_accelerators || []
        }

        // Build people filters (same as searchPeople, but WITHOUT person_titles)
        const peopleFilters = {
            person_countries: expandedCountries,
            person_locations: filters.person_locations || [],
            person_titles: [], // Intentionally empty - we're suggesting titles
            person_seniorities: filters.person_seniorities || [],
            person_functions: [],
            alumni_schools: filters.alumni_schools || [],
            fields_of_study: filters.fields_of_study || [],
            years_out_of_college: filters.years_out_of_college || [],
            years_at_current_company: filters.years_at_current_company || [],
            linkedin_summary_keywords: filters.linkedin_summary_keywords || [],
            min_linkedin_connections: filters.min_linkedin_connections,
            contact_email_status: [],
            current_companies: filters.current_companies || [],
            previous_companies: filters.previous_companies || [],
            company_location_filters: filters.company_locations || [],
            company_employee_counts: filters.company_employee_counts || [],
            company_revenue_ranges: filters.company_revenue_ranges || [],
            company_stage: filters.company_stage || [],
            company_investors: filters.company_investors || [],
            company_industries: filters.company_industries || [],
            company_fiber_industries: [],
            company_linkedin_industries: filters.company_linkedin_industries || [],
            company_filter_mode: filters.company_filter_mode || "venture",
            exclude_company_list_ids: filters.excludeCompanyListIds || [],
            exclude_linkedin_urls: []
        }

        const response = await fetch(`${API_URL}/people/suggest-titles`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({
                people_filters: peopleFilters,
                company_filters: companyFilters,
                input_title: params.inputTitle
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`)
        }

        const data = await response.json()
        console.log("[api.ts] suggestTitles response:", data)

        return data.suggestions || []
    } catch (error) {
        console.error("[api.ts] suggestTitles error:", error)
        return []
    }
}

// ============================================================================
// Exa (Lookalike) API Functions
// ============================================================================

export interface ExaPreviewParams {
    query: string
    excludeWebsetIds?: string[]
}

export interface ExaPreviewResult {
    status: string
    entity_type: string
    criteria: Array<{ description: string; successRate?: number }>
    preview_items: Array<{ name: string; url: string; description?: string }>
    enrichments: unknown[]
}

export async function exaPreview(params: ExaPreviewParams): Promise<ExaPreviewResult> {
    console.log("[api.ts] exaPreview called with:", params)

    const response = await fetch(`${API_URL}/exa/preview`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
            query: params.query,
            exclude_webset_ids: params.excludeWebsetIds || []
        })
    })

    if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Exa preview failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log("[api.ts] exaPreview response:", data)
    return data
}

export interface ExaCreateWebsetParams {
    query: string
    count?: number
    criteria?: Array<{ description: string }>
    excludeWebsetIds?: string[]
}

export interface ExaCreateWebsetResult {
    status: string
    webset_id: string
    message: string
}

export async function exaCreateWebset(params: ExaCreateWebsetParams): Promise<ExaCreateWebsetResult> {
    console.log("[api.ts] exaCreateWebset called with:", params)

    const response = await fetch(`${API_URL}/exa/create-webset`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
            query: params.query,
            count: params.count || 50,
            criteria: params.criteria,
            exclude_webset_ids: params.excludeWebsetIds || []
        })
    })

    if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Exa create webset failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log("[api.ts] exaCreateWebset response:", data)
    return data
}

export interface ExaWebsetStatusResult {
    status: string // 'idle', 'pending', 'running', 'completed', 'canceled'
    webset_id: string
    progress?: {
        found: number
        analyzed: number
        completion: number
        timeLeft?: string
    }
    companies?: Array<{
        company_name: string
        company_description: string
        website: string
        linkedin_url?: string
    }>
    total_count?: number
}

export async function exaGetWebsetStatus(websetId: string): Promise<ExaWebsetStatusResult> {
    const response = await fetch(`${API_URL}/exa/webset-status/${websetId}`, {
        headers: await authHeaders()
    })

    if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Exa get status failed: ${response.status} - ${errorText}`)
    }

    return response.json()
}

export async function exaCancelWebset(websetId: string): Promise<{ status: string; message: string }> {
    console.log("[api.ts] exaCancelWebset called with:", websetId)

    const response = await fetch(`${API_URL}/exa/cancel-webset/${websetId}`, {
        method: "POST",
        headers: await authHeaders()
    })

    if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Exa cancel failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log("[api.ts] exaCancelWebset response:", data)
    return data
}

// ============================================
// SAVE EXA COMPANIES
// ============================================

export interface SaveExaCompaniesParams {
    listName: string
    orgDomain: string
    ownerEmail?: string
    companies: Array<{
        company_name: string
        company_description?: string
        website: string
        linkedin_url?: string
    }>
    websetId?: string
    exaSearch?: string  // Natural language search query
}

export interface SaveExaCompaniesResult {
    listId: number
    savedCount: number
}

export async function saveExaCompanies(params: SaveExaCompaniesParams): Promise<SaveExaCompaniesResult> {
    console.log("[api.ts] saveExaCompanies called with:", {
        listName: params.listName,
        orgDomain: params.orgDomain,
        companyCount: params.companies.length
    })

    const response = await fetch(`${API_URL}/save-companies`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
            list_name: params.listName,
            org_domain: params.orgDomain,
            owner_email: params.ownerEmail,
            companies: params.companies,
            webset_id: params.websetId,
            exa_search: params.exaSearch
        })
    })

    if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Save Exa companies failed: ${response.status} - ${errorText}`)
    }

    const data = await response.json()
    console.log("[api.ts] saveExaCompanies response:", data)
    return {
        listId: data.list_id,
        savedCount: data.saved_count
    }
}


// ============================================
// DATA SOURCE RECOMMENDATION
// ============================================

export interface DataSourceRecommendation {
    use_google_search: boolean
    use_website_scrape: boolean
    use_employee_info: boolean
    use_linkedin_employees: boolean
    use_job_postings: boolean
    use_linkedin_posts: boolean
    use_tech_stack: boolean
    use_linkedin_company: boolean
    use_screenshot: boolean
    reasoning: string
}

/**
 * Recommend which data sources to enable for an AI filter question.
 * Uses a lightweight Claude call to analyze the question.
 */
export async function recommendDataSources(question: string, listType?: "person" | "company"): Promise<DataSourceRecommendation> {
    console.log("[api.ts] recommendDataSources called with:", question, "listType:", listType)

    try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort("recommend-data-sources timeout (10s)"), 10000)

        const response = await fetch(`${API_URL}/agent/recommend-data-sources`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({ question, list_type: listType }),
            signal: controller.signal,
        })
        clearTimeout(timeoutId)

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Recommend data sources failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] recommendDataSources response:", data)

        const result = {
            use_google_search: data.use_google_search ?? false,
            use_website_scrape: data.use_website_scrape ?? false,
            use_employee_info: data.use_employee_info ?? false,
            use_linkedin_employees: data.use_linkedin_employees ?? false,
            use_job_postings: data.use_job_postings ?? false,
            use_linkedin_posts: data.use_linkedin_posts ?? false,
            use_tech_stack: data.use_tech_stack ?? false,
            use_linkedin_company: data.use_linkedin_company ?? false,
            use_screenshot: data.use_screenshot ?? false,
            reasoning: data.reasoning ?? ""
        }
        console.log(`[api.ts] Data source recommendation reasoning: ${result.reasoning}`)
        console.log(`[api.ts] employee_info=${result.use_employee_info}, google=${result.use_google_search}, website=${result.use_website_scrape}`)
        return result
    } catch (error) {
        // AbortError is expected when the recommendation deadline fires before the response arrives
        const isAbort = (error instanceof DOMException && error.name === "AbortError") ||
            (typeof error === "string" && error.includes("timeout"))
        if (isAbort) {
            console.log("[api.ts] recommendDataSources aborted (deadline exceeded)")
        } else {
            console.error("[api.ts] recommendDataSources error:", error)
        }
        // Default fallback: just google search
        return {
            use_google_search: true,
            use_website_scrape: false,
            use_employee_info: false,
            use_linkedin_employees: false,
            use_job_postings: false,
            use_linkedin_posts: false,
            use_tech_stack: false,
            use_linkedin_company: false,
            use_screenshot: false,
            reasoning: "fallback - api error"
        }
    }
}

// ============================================
// NATURAL LANGUAGE SEARCH (Agent)
// ============================================

export interface NLSearchResult {
    status: "completed" | "needs_confirmation" | "error"
    mode: "people" | "companies"
    company_filters: Record<string, any>
    people_filters: Record<string, any>
    ai_filters?: AIFilter[]
    summary?: string  // Human-readable summary for confirmation
    message?: string
    // Reverse search fields
    is_reverse_search?: boolean
    reference_companies?: string[]  // Domains used as reference (e.g., ["stripe.com", "notion.com"])
    reference_industries?: string[]  // LinkedIn industries extracted from reference companies
}

export interface ConversationMessage {
    role: "user" | "assistant"
    content: string
}

/**
 * Natural language search - sends query to agent which determines if it's
 * a people or company search and returns appropriate filters.
 *
 * Supports a confirmation flow:
 * - Initial request: Returns status "needs_confirmation" with a summary
 * - Follow-up request (with conversation_history): Returns "completed" if confirmed, or new "needs_confirmation" if modified
 */
export async function nlSearch(
    query: string,
    domain?: string,
    conversationHistory?: ConversationMessage[]
): Promise<NLSearchResult> {
    try {
        // Load countries data to send with request
        const countriesData = await loadCountriesData()

        const response = await fetch(`${API_URL}/agent/nl-search`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({
                query,
                countries_json: countriesData,
                domain,
                conversation_history: conversationHistory
            })
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`NL search failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] nlSearch response:", data)

        return {
            status: data.status,
            mode: data.mode,
            company_filters: data.company_filters || {},
            people_filters: data.people_filters || {},
            ai_filters: data.ai_filters || [],
            summary: data.summary,
            message: data.message,
            is_reverse_search: data.is_reverse_search || false,
            reference_companies: data.reference_companies || [],
            reference_industries: data.reference_industries || []
        }
    } catch (error) {
        console.error("[api.ts] nlSearch error:", error)
        throw error
    }
}

/**
 * Reverse lookup - Get LinkedIn industries for a domain
 * Used for "reverse search" in filter panels
 */
export interface ReverseLookupResult {
    linkedin_industries: string[]
    company_name: string | null
    found: boolean
}

export async function reverseLookup(domain: string): Promise<ReverseLookupResult> {
    console.log("[api.ts] reverseLookup called for domain:", domain)
    try {
        const response = await fetch(`${API_URL}/agent/reverse-lookup`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({ domain })
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Reverse lookup failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        console.log("[api.ts] reverseLookup response:", data)

        return {
            linkedin_industries: data.linkedin_industries || [],
            company_name: data.company_name || null,
            found: data.found || false
        }
    } catch (error) {
        console.error("[api.ts] reverseLookup error:", error)
        throw error
    }
}
